/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/Organizations.tsx
 * DESCRIPTION: Organization workspace management page (Phase 3 rewrite).
 *              Fully wired to live Clerk APIs — all mock data removed.
 *              Manages members, invitations, departments, and settings,
 *              plus an org-scoped Overview and Library via tabs.
 *
 * FEATURES:
 *   - Overview tab: live stats (members, files, departments, plan)
 *   - Library tab: org-scoped media library via listIdentities({org_id})
 *   - Members tab: real member list, role management, department
 *     assignment, invitations with revoke
 *   - Settings tab: real org name/slug editing via organization.update()
 *   - Activity tab: clean empty state (logging arrives with Phase 4)
 *   - Permission gating: invite/role/remove/settings restricted to
 *     owner & admin roles
 *   - Dark mode + mobile responsive throughout
 *
 * CLERK API INTEGRATION (replaces all mocks):
 *   - useOrganization({ memberships, invitations }) → live data
 *   - organization.invitations.create()             → send invitation
 *   - membership.update({ role })                   → change member role
 *   - membership.destroy()                          → remove member
 *   - invitation.revoke()                           → revoke invite
 *   - organization.update({ name, slug })           → save settings
 *   - organization.update({ unsafeMetadata })       → store departments (Beta)
 *
* DEPARTMENTS NOTE (Beta):
*   Department assignments live in org.publicMetadata.departments as a
*   map of userId → departmentId. For organizations (unlike users),
*   publicMetadata is writable from the client via organization.update().
*   Enforcement migrates to Supabase in Phase 5. 
* 
 * ROADMAP STATUS:
 *   - Phase 3.1 Real member list                
 *   - Phase 3.2 Real org settings               
 *   - Phase 3.3 Real invite flow                
 *   - Phase 3.4 Real role change + removal      
 *   - Phase 3.5 Library + Overview tabs         
 *   - Phase 3.6 Department assignment           
 *   - Activity logging                          ⏳ Phase 4
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 3.0.0
 * @created 2025-01-15
 * @updated 2026-09-05 - Complete Phase 3 rewrite: removed all mock data,
 *                       wired live Clerk APIs, added Overview + Library tabs,
 *                       department assignment, permission gating, and
 *                       Activity empty state.
 * ─────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useOrganization } from '@clerk/react';
import {
  Users, Settings, Activity, Plus, Mail, Shield, Trash2, Check, X,
  Clock, Building2, CreditCard, FileText, LayoutDashboard, Library,
  RefreshCw, AlertCircle, UserPlus, FileVideo, FileImage, FileAudio,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getApiClient } from '../lib/api/apiClient';
import type { VideoIdentity } from '../lib/api/types';
import { ORG_DEPARTMENTS, getDepartmentLabel } from '../lib/constants/departments';

// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────
type OrgRole = 'owner' | 'admin' | 'member';
type TabId = 'overview' | 'library' | 'members' | 'settings' | 'activity';

interface OrgMember {
  id: string;              // membership id
  userId: string;
  name: string;
  email: string;
  role: OrgRole;
  avatar?: string;
  joinedAt: number;
  isCurrent: boolean;
  resource: any;           // Clerk membership resource (update/destroy)
}

interface OrgInvitation {
  id: string;
  email: string;
  role: OrgRole;
  status: string;
  createdAt: number;
  resource: any;           // Clerk invitation resource (revoke)
}

// ─────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────
const getRoleBadge = (role: OrgRole) => {
  const badges: Record<OrgRole, string> = {
    owner:  'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    admin:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    member: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400',
  };
  return badges[role];
};

const formatDate = (ts: number) =>
  ts ? new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const fileTypeIcon = (type: string) => {
  switch (type) {
    case 'video': return FileVideo;
    case 'image': return FileImage;
    case 'audio': return FileAudio;
    default: return FileText;
  }
};

// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const Organizations: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const {
    organization,
    membership,
    isLoaded,
    memberships,
    invitations,
  } = useOrganization({
    memberships: true,
    invitations: true,
  });

  // ── UI state ─────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('member');
  const [isProcessing, setIsProcessing] = useState(false);

  // Settings form state
  const [settingsName, setSettingsName] = useState('');
  const [settingsSlug, setSettingsSlug] = useState('');

  // Org-scoped stats + library
  const [fileCount, setFileCount] = useState(0);
  const [orgFiles, setOrgFiles] = useState<VideoIdentity[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  // ── Derived values ───────────────────────────────────────────────
  const currentRole = (membership?.role as OrgRole) ?? 'member';
  const canManage = currentRole === 'owner' || currentRole === 'admin';
  const isPersonalOrg = organization?.slug?.startsWith('personal-') ?? false;

  // Department map: userId → departmentId (stored in org unsafeMetadata)
  const departmentsMap: Record<string, string> =
    ((organization?.publicMetadata as any)?.departments) ?? {};
  const departmentCount = new Set(Object.values(departmentsMap)).size;

  // Map Clerk memberships → display-friendly members
  const members: OrgMember[] = (memberships?.data ?? []).map((m: any) => ({
    id: m.id,
    userId: m.publicUserData?.userId ?? '',
    name:
      [m.publicUserData?.firstName, m.publicUserData?.lastName].filter(Boolean).join(' ') ||
      m.publicUserData?.identifier || 'Member',
    email: m.publicUserData?.identifier ?? '',
    role: (m.role as OrgRole) ?? 'member',
    avatar: m.publicUserData?.imageUrl,
    joinedAt: m.createdAt ? new Date(m.createdAt).getTime() : 0,
    isCurrent: m.publicUserData?.userId === user?.id,
    resource: m,
  }));

  // Map Clerk invitations → pending invites
  const pendingInvites: OrgInvitation[] = (invitations?.data ?? []).map((inv: any) => ({
    id: inv.id,
    email: inv.emailAddress ?? '',
    role: (inv.role as OrgRole) ?? 'member',
    status: inv.status ?? 'pending',
    createdAt: inv.createdAt ? new Date(inv.createdAt).getTime() : 0,
    resource: inv,
  }));

  // ── Load org stats (file count) ──────────────────────────────────
  useEffect(() => {
    const loadStats = async () => {
      if (!organization?.id) return;
      try {
        const apiClient = getApiClient();
        const res = await apiClient.listIdentities({ org_id: organization.id, limit: 1 });
        setFileCount(res.total || 0);
      } catch (e) {
        // Stats are non-critical; fail silently
        console.warn('[Organizations] Failed to load org stats:', e);
      }
    };
    loadStats();
  }, [organization?.id]);

  // ── Init settings form when org changes ──────────────────────────
  useEffect(() => {
    if (organization) {
      setSettingsName(organization.name || '');
      setSettingsSlug(organization.slug || '');
    }
  }, [organization?.id]);

  // ── Load org library (lazy: only when Library tab opens) ─────────
  const loadOrgLibrary = useCallback(async () => {
    if (!organization?.id) return;
    setIsLoadingFiles(true);
    try {
      const apiClient = getApiClient();
      const res = await apiClient.listIdentities({ org_id: organization.id, limit: 24 });
      setOrgFiles(res.identities || []);
    } catch (e) {
      console.error('[Organizations] Failed to load library:', e);
      toast.error('Failed to load organization library');
    } finally {
      setIsLoadingFiles(false);
    }
  }, [organization?.id]);

  useEffect(() => {
    if (activeTab === 'library' && organization?.id) {
      loadOrgLibrary();
    }
  }, [activeTab, organization?.id, loadOrgLibrary]);

  // ───────────────────────────────────────────────────────────────────
  // HANDLERS (real Clerk API calls)
  // ───────────────────────────────────────────────────────────────────
  const handleInviteMember = async () => {
    if (!organization || !inviteEmail.trim()) return;

    setIsProcessing(true);

    try {
      await organization.inviteMember({
        emailAddress: inviteEmail.trim(),
        role: inviteRole,
      });

      toast.success(`Invitation sent to ${inviteEmail.trim()}`);

      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('member');

      invitations?.revalidate();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send invitation');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRoleChange = async (member: OrgMember, newRole: OrgRole) => {
    try {
      await member.resource.update({ role: newRole });
      toast.success('Role updated');
      memberships?.revalidate();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (member: OrgMember) => {
    if (!window.confirm(`Remove ${member.name} from this organization?`)) return;
    try {
      await member.resource.destroy();
      toast.success('Member removed');
      memberships?.revalidate();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to remove member');
    }
  };

  const handleRevokeInvite = async (inv: OrgInvitation) => {
    try {
      await inv.resource.revoke();
      toast.success('Invitation revoked');
      invitations?.revalidate();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to revoke invitation');
    }
  };

  const handleUpdateSettings = async () => {
    if (!organization) return;
    setIsProcessing(true);
    try {
      await organization.update({
        name: settingsName,
        ...(isPersonalOrg ? {} : { slug: settingsSlug }), // Protect personal-org slug
      });
      toast.success('Settings saved');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save settings');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Assign a department to a member. Stored in org.unsafeMetadata.departments.
   * NOTE: Cast to `any` to guarantee compilation across Clerk SDK versions.
   */
  const handleAssignDepartment = async (userId: string, deptId: string) => {
  if (!organization) return;
  try {
    await organization.update({
      publicMetadata: {
        ...((organization.publicMetadata as any) || {}),
        departments: { ...departmentsMap, [userId]: deptId },
      },
    } as any);
    toast.success('Department updated');
  } catch (error: any) {
    console.error('[Organizations] Department assignment failed:', error);
    toast.error('Could not save department — enforcement arrives in Phase 5');
  }
};

  // ───────────────────────────────────────────────────────────────────
  // RENDER — Loading
  // ───────────────────────────────────────────────────────────────────
  if (!isLoaded) {
    return (
      <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // No active organization
  if (!organization) {
    return (
      <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto text-center py-24">
          <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No organization found</h2>
          <p className="text-gray-600 dark:text-gray-400">Create or join an organization to get started.</p>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────
  // RENDER — Tab config
  // ───────────────────────────────────────────────────────────────────
  const tabs: { id: TabId; label: string; icon: any; count?: number }[] = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'library', label: 'Library', icon: Library, count: fileCount },
    { id: 'members', label: 'Members', icon: Users, count: members.length },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'activity', label: 'Activity', icon: Activity },
  ];

  return (
    <div className="p-4 sm:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Organization</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your workspace, members, and shared library
          </p>
        </div>

        {/* Personal workspace notice */}
        {isPersonalOrg && (
          <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-300">You're viewing your personal workspace</p>
              <p className="text-sm text-blue-700 dark:text-blue-400 mt-0.5">
                Create a team organization to collaborate and share a library with colleagues.
              </p>
            </div>
          </div>
        )}

        {/* Organization identity card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{organization.name}</h2>
                <p className="text-gray-600 dark:text-gray-400">@{organization.slug} • {currentRole}</p>
              </div>
            </div>
            {canManage && (
              <button
                onClick={() => setActiveTab('settings')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
              >
                <Settings className="w-5 h-5" />
                Edit Settings
              </button>
            )}
          </div>
        </div>

        {/* Tab bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto">
            <nav className="flex gap-6 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-2 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count !== undefined && (
                    <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* ═══════════════ OVERVIEW TAB ═══════════════ */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Members', value: members.length, sub: `${pendingInvites.length} pending`, icon: Users, color: 'blue' },
                { label: 'Files Analyzed', value: fileCount, sub: 'this workspace', icon: FileText, color: 'purple' },
                { label: 'Departments', value: departmentCount, sub: 'assigned', icon: Shield, color: 'emerald' },
                { label: 'Plan', value: 'Free', sub: 'Beta — upgrade soon', icon: CreditCard, color: 'orange' },
              ].map((stat) => (
                <div key={stat.label} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${
                      stat.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                      stat.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/30' :
                      stat.color === 'emerald' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                      'bg-orange-100 dark:bg-orange-900/30'
                    }`}>
                      <stat.icon className={`w-5 h-5 ${
                        stat.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
                        stat.color === 'purple' ? 'text-purple-600 dark:text-purple-400' :
                        stat.color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' :
                        'text-orange-600 dark:text-orange-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{stat.sub}</p>
                </div>
              ))}
            </div>
          )}

          {/* ═══════════════ LIBRARY TAB ═══════════════ */}
          {activeTab === 'library' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Shared Library</h2>
                <button
                  onClick={loadOrgLibrary}
                  disabled={isLoadingFiles}
                  className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoadingFiles ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {isLoadingFiles ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : orgFiles.length === 0 ? (
                <div className="text-center py-16">
                  <Library className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No files in this workspace yet</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">Files uploaded to this organization will appear here.</p>
                  <button
                    onClick={() => navigate('/analyze')}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Upload Files
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {orgFiles.map((file) => {
                    const TypeIcon = fileTypeIcon(file.file_type);
                    const thumbnail = (file as any).thumbnail_url || file.storage_url;
                    return (
                      <div
                        key={file.persistent_id}
                        onClick={() => navigate(`/identities/${file.persistent_id}`)}
                        className="group bg-white dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden hover:shadow-lg transition-all cursor-pointer"
                      >
                        <div className="relative aspect-video bg-gray-100 dark:bg-gray-700">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={file.original_filename || ''}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                              <TypeIcon className="w-8 h-8" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="font-medium text-gray-900 dark:text-white truncate text-sm">
                            {file.original_filename || 'Unknown'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {file.file_type?.toUpperCase()} • {formatDate(new Date(file.created_at).getTime())}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ═══════════════ MEMBERS TAB ═══════════════ */}
          {activeTab === 'members' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Organization Members</h2>
                {canManage && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    <UserPlus className="w-5 h-5" />
                    Invite Member
                  </button>
                )}
              </div>

              {/* Members table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Member</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Role</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Department</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400 hidden md:table-cell">Joined</th>
                      {canManage && <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {members.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        {/* Member */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            {member.avatar ? (
                              <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <span className="text-blue-600 dark:text-blue-400 font-medium">
                                  {member.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {member.name} {member.isCurrent && <span className="text-xs text-gray-400">(you)</span>}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{member.email}</p>
                            </div>
                          </div>
                        </td>

                        {/* Role */}
                        <td className="py-4 px-4">
                          {member.role === 'owner' || !canManage || member.isCurrent ? (
                            <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full capitalize ${getRoleBadge(member.role)}`}>
                              {member.role}
                            </span>
                          ) : (
                            <select
                              value={member.role}
                              onChange={(e) => handleRoleChange(member, e.target.value as OrgRole)}
                              className={`px-3 py-1 rounded-full text-xs font-medium border-none cursor-pointer capitalize ${getRoleBadge(member.role)}`}
                            >
                              <option value="admin">Admin</option>
                              <option value="member">Member</option>
                            </select>
                          )}
                        </td>

                        {/* Department */}
                        <td className="py-4 px-4">
                          {canManage ? (
                            <select
                              value={departmentsMap[member.userId] || ''}
                              onChange={(e) => handleAssignDepartment(member.userId, e.target.value)}
                              className="px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">No department</option>
                              {ORG_DEPARTMENTS.map((d) => (
                                <option key={d.id} value={d.id}>{d.label}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {departmentsMap[member.userId] ? getDepartmentLabel(departmentsMap[member.userId]) : '—'}
                            </span>
                          )}
                        </td>

                        {/* Joined */}
                        <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell">
                          {formatDate(member.joinedAt)}
                        </td>

                        {/* Actions */}
                        {canManage && (
                          <td className="py-4 px-4">
                            {member.role !== 'owner' && !member.isCurrent && (
                              <button
                                onClick={() => handleRemoveMember(member)}
                                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                title="Remove member"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pending invitations */}
              {pendingInvites.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pending Invitations</h3>
                  <div className="space-y-3">
                    {pendingInvites.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                            <Mail className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{inv.email}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                              Invited as {inv.role} • {formatDate(inv.createdAt)}
                            </p>
                          </div>
                        </div>
                        {canManage && (
                          <button
                            onClick={() => handleRevokeInvite(inv)}
                            className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          >
                            Revoke
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════ SETTINGS TAB ═══════════════ */}
          {activeTab === 'settings' && (
            <div className="max-w-2xl">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Organization Settings</h2>

              {!canManage ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-400">
                  Only owners and admins can edit organization settings.
                </div>
              ) : (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Organization Name</label>
                    <input
                      type="text"
                      value={settingsName}
                      onChange={(e) => setSettingsName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Organization Slug</label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 dark:text-gray-400">verimark.app/</span>
                      <input
                        type="text"
                        value={settingsSlug}
                        onChange={(e) => setSettingsSlug(e.target.value)}
                        disabled={isPersonalOrg}
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                    {isPersonalOrg && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Personal workspace slugs are locked to preserve workspace identity.
                      </p>
                    )}
                  </div>

                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleUpdateSettings}
                      disabled={isProcessing || !settingsName.trim()}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      {isProcessing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="w-5 h-5" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══════════════ ACTIVITY TAB ═══════════════ */}
          {activeTab === 'activity' && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Activity className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Activity logging arrives soon</h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                A full audit trail of member actions, uploads, and settings changes will be available
                once backend logging lands in Phase 4.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════ INVITE MEMBER MODAL ═══════════════ */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowInviteModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Invite Member</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="member@company.com"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Role</label>
                <div className="grid grid-cols-2 gap-3">
                  {(['admin', 'member'] as const).map((role) => (
                    <button
                      key={role}
                      onClick={() => setInviteRole(role)}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        inviteRole === role
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center justify-center mb-2">
                        <Shield className={`w-5 h-5 ${role === 'admin' ? 'text-blue-600' : 'text-gray-600'}`} />
                      </div>
                      <p className={`text-sm font-medium capitalize ${
                        inviteRole === role ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {role}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteMember}
                disabled={isProcessing || !inviteEmail.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Organizations;